import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { ASSETS } from "@/config";

// Full-screen boot sequence — runs once per browser session.
export default function BootSequence() {
  const { t, lang } = useLanguage();
  const [visible, setVisible] = useState(() => {
    try {
      return !sessionStorage.getItem("mizukara-booted");
    } catch {
      return true;
    }
  });
  const [step, setStep] = useState(0);
  const [fading, setFading] = useState(false);

  const lines = t("boot.lines");

  const finish = () => {
    try {
      sessionStorage.setItem("mizukara-booted", "1");
    } catch {
      /* ignore */
    }
    setFading(true);
    setTimeout(() => setVisible(false), 650);
  };

  useEffect(() => {
    if (!visible) return undefined;
    if (step < lines.length) {
      const timer = setTimeout(() => setStep((s) => s + 1), step === 0 ? 500 : 620);
      return () => clearTimeout(timer);
    }
    const end = setTimeout(finish, 900);
    return () => clearTimeout(end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, visible, lang]);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="boot-sequence"
        className="fixed inset-0 z-[100] flex cursor-pointer flex-col items-center justify-center bg-[#010409]"
        initial={{ opacity: 1 }}
        animate={{ opacity: fading ? 0 : 1 }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        onClick={finish}
      >
        <motion.img
          src={ASSETS.logo}
          alt="MIZUKARA"
          className="mb-10 h-28 w-28 rounded-full opacity-90 animate-pulse-glow"
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.9 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          data-testid="boot-logo"
        />
        <div className="w-[min(90vw,420px)] font-mono text-sm leading-7 text-mizu-cyan/90 mizu-glow">
          {lines.slice(0, step).map((l, i) => (
            <motion.div
              key={`${lang}-${i}`}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25 }}
              data-testid={`boot-line-${i}`}
            >
              {l}
              {i === step - 1 && step < lines.length && (
                <span className="animate-blink">_</span>
              )}
            </motion.div>
          ))}
          {step >= lines.length && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 font-display tracking-[0.3em] text-white"
              data-testid="boot-online"
            >
              {t("boot.online")}
            </motion.div>
          )}
        </div>
        <div className="absolute bottom-8 font-mono text-[10px] tracking-[0.3em] text-mizu-faint">
          {t("boot.skip")}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
